import type React from "react";
import { cn } from "../../lib/utils";
import { iconVariants, type IconColor, type IconSize } from "./icon";
import { useDelayedFade } from "./skeleton";

/**
 * Per-cell start offsets in ms, reading order — left to right, top to
 * bottom:
 *
 * ```
 *   0 1 2
 *   3 4 5
 *   6 7 8
 * ```
 */
export type GridSpinnerDelays = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

export type GridSpinnerAnimationSpec = {
  /** Start offset in ms for each of the nine cells, reading order. */
  delays: GridSpinnerDelays;
  /** How long a lit cell holds before it starts to fade, in ms. */
  duration?: number;
};

/**
 * The preset patterns. Each is just a delay array — the order the cells
 * light — plus a hold, so a custom pattern is the same shape.
 */
export const GRID_SPINNER_ANIMATIONS = {
  /** Clockwise around the outer ring, center last. */
  orbit: {
    delays: [0, 80, 160, 560, 640, 240, 480, 400, 320],
    duration: 120,
  },
  /** The two interleaved cell sets alternate. */
  checkerboard: {
    delays: [0, 360, 0, 360, 0, 360, 0, 360, 0],
    duration: 240,
  },
} as const satisfies Record<string, GridSpinnerAnimationSpec>;

export type GridSpinnerAnimationName = keyof typeof GRID_SPINNER_ANIMATIONS;

/* The tail a fading cell needs after its hold ends. Part of the loop length,
   so the last cell finishes dimming before the first lights again. */
const GRID_SPINNER_FADE_MS = 400;

export type GridSpinnerProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "color" | "children"
> & {
  /** A preset name, or a custom delay array in the same shape. */
  animation?: GridSpinnerAnimationName | GridSpinnerAnimationSpec;
  /** The Icon size tokens, so it swaps 1:1 with Spinner. */
  size?: IconSize;
  /**
   * Each gutter's share of the overall box, in percent (0–30). The box is
   * fixed by `size`, so a smaller gap means larger squares: 0 packs the
   * cells into one solid square, 10 is the resting ratio, 30 reads as nine
   * separated dots. Proportional, so every `size` keeps the same silhouette.
   */
  gap?: number;
  color?: IconColor;
  /** The same anti-flash gate as Spinner's `delayed`. */
  delayed?: boolean;
};

/**
 * The 3×3 pixel-grid loading indicator: nine cells sharing one opacity
 * pulse, offset per cell so a pattern travels the grid. An ambient
 * alternative to Spinner for the same waits — same size tokens, same
 * `color` routing, and it inherits `currentColor` by default just as
 * Spinner does.
 */
export function GridSpinner({
  animation = "orbit",
  size = "xl",
  gap = 10,
  color,
  delayed = false,
  className,
  style,
  "aria-label": ariaLabel = "Loading",
  ...props
}: GridSpinnerProps): React.ReactElement {
  const spec =
    typeof animation === "string"
      ? GRID_SPINNER_ANIMATIONS[animation]
      : animation;
  const duration = spec.duration ?? 120;
  const cycle = Math.max(...spec.delays) + duration + GRID_SPINNER_FADE_MS;
  /* Past 30% per gutter the three 1fr cells hit zero width. */
  const gapPercent = Math.min(Math.max(gap, 0), 30);
  const dataColor =
    color === "subtle" || color === "faint" ? undefined : color;
  const fade = useDelayedFade(delayed);

  return (
    <div
      aria-label={ariaLabel}
      className={cn(
        iconVariants({ size }),
        "grid grid-cols-3 grid-rows-3",
        color === "subtle" && "text-muted-foreground/72",
        color === "faint" && "text-faint-foreground",
        dataColor && "text-c",
        fade,
        className,
      )}
      data-color={dataColor}
      data-slot="grid-spinner"
      role="status"
      style={
        {
          "--grid-spinner-cycle": `${cycle}ms`,
          /* Percent-of-self, so the ratio survives every size token; the
             box is square, so one value gaps both axes evenly. The 1px
             floor keeps a nonzero gutter from smearing at the small size
             tokens, where a fraction of a pixel reads as a solid blob. */
          gap: gapPercent === 0 ? "0px" : `max(1px, ${gapPercent}%)`,
          ...style,
        } as React.CSSProperties
      }
      {...props}
    >
      {spec.delays.map((delay, cell) => (
        <div
          key={cell}
          className="animate-grid-spinner bg-current motion-reduce:animate-none"
          /* Negative: every cell is already mid-loop on its first frame, so
             the grid never opens with a beat of nine dim squares. Reduced
             motion drops the animation and the cells rest at full opacity —
             a static grid glyph. */
          style={{ animationDelay: `${delay - cycle}ms` }}
        />
      ))}
    </div>
  );
}
